import { useMedplum } from '@medplum/react';
import { useCallback, useState } from 'react';
import { fetchBot } from '@/services/services';
import { Patient, Reference, Task } from '@medplum/fhirtypes';
import { CreateClinicalTaskInput } from '@bariendo/lib/task-management';

export type CreateClinicalTaskFields = Omit<CreateClinicalTaskInput, 'patient'>;

/**
 * `patient` may be undefined while the caller is still choosing one — the
 * inbox's create form mounts before a patient is picked, unlike the patient
 * timeline where it is implied. createTask throws rather than sending a
 * patient-less task, so a mis-wired caller fails loudly instead of writing
 * an orphan.
 */
export function useCreateTask(patient: Reference<Patient> | undefined) {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createTask = useCallback(
    async (fields: CreateClinicalTaskFields): Promise<Task> => {
      if (!patient) {
        throw new Error('Cannot create a task without a patient');
      }
      setLoading(true);
      setError(undefined);
      try {
        const task = await fetchBot<Task>(medplum, 'create-clinical-task', {
          patient,
          ...fields,
        });
        return task;
      } catch (e) {
        const err = e instanceof Error ? e : new Error('Unknown error');
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [medplum, patient]
  );

  return { createTask, loading, error };
}
